<template>
  <TTDialog v-model="dialog" @input="willCloseDialog" data-cy="bulk-update-dialog">
    <template #header>
      <div>
        <div class="d-flex flex-row">
          <div style="margin-right: 15px">
            <h3 class="font-weight-bold">Bulk {{ isDoneProcessing ? 'Updates' : selectedOperation?.name }}</h3>
          </div>

          <div
            v-if="isDoneProcessing"
            style="
              background-color: #f78e1e;
              padding: 2px 5px 2px 5px;
              border-radius: 5px;
              margin-right: 15px;
              height: 36px;
            "
          >
            {{ String(selectedOperation?.label).toUpperCase() }}
          </div>

          <v-text-field
            v-if="selectedOperation.value === 'mass-send'"
            v-model="operationData.checkNumber"
            style="margin-top: -10px; margin-right: 15px"
            v-bind="inputProps"
            :loading="isProcessing"
            label="Check #"
          ></v-text-field>

          <v-text-field
            v-if="selectedOperation.value === 'mass-pay'"
            v-model="operationData.batchJournalNumber"
            style="margin-top: -10px; margin-right: 15px"
            :loading="isProcessing"
            v-bind="inputProps"
            label="Batch Journal #"
          ></v-text-field>
          <v-btn data-cy="invoice-bulk-update-process-btn" :loading="isProcessing" color="#f78e1e" @click="process()">
            {{ selectedOperation?.name }}
          </v-btn>
        </div>
        <div class="d-flex flex-row" style="font-size: medium; font-weight: normal !important">
          <div v-if="selectedOperation.showTotals" style="margin-right: 15px">
            <span
              >{{ selectedCount }} items selected:
              <span class="font-weight-bold">{{ toDollarAmount(selectedInvoicesTotal) }}</span></span
            >
          </div>
          <div v-if="isDoneProcessing && failedUpdates.length">
            <span style="color: red">{{ failedUpdates.length }} errors</span>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div>
        <v-data-table
          v-if="!selectedInvoices.length"
          :headers="headers"
          :items="operationData.invoices"
          :items-per-page="pageSetup.pageSize"
          :footer-props="{
            'items-per-page-options': [8, 16],
          }"
          :options.sync="pageOptions"
          show-select
          v-model="operationData.selected"
          @item-selected="setSelection"
          class="elevation-1"
          @toggle-select-all="selectionChangedViaCheckbox"
          :page.sync="pageSetup.currentPage"
          @pagination="pageChanged"
          :server-items-length="Number(pageSetup.length)"
          :loading="isTableLoading"
        >
          <template v-slot:header.data-table-select="{ on, props }">
            <v-simple-checkbox v-if="!isSingleSelect" v-bind="props" v-on="on"></v-simple-checkbox>
          </template>
          <template v-slot:item.tripRequestId="{ header, item }">
            <span class="font-weight-bold" style="color: #4066a1">{{ item.tripRequestId }}</span>
          </template>
          <template v-slot:item.total="{ header, item }">
            {{ toDollarAmount(item.total) }}
          </template>
          <template v-slot:item.status="{ header, item }">
            <v-chip
              data-cy="invoice-bulk-update-status"
              :item-id="item.id"
              :color="getStatus(header, item).color"
              small
              class="font-weight-bold"
              label
            >
              {{ getStatus(header, item).text }}
            </v-chip>
          </template>
          <template #item.data-table-select="{ isSelected, select, item }">
            <v-checkbox
              v-if="item.status !== selectedOperation.yields"
              :input-value="isSelected"
              @click="select(!isSelected)"
              hide-details
              dense
              data-cy="invoice-bulk-update-checkbox"
              :item-id="item.id"
            ></v-checkbox>
          </template>
        </v-data-table>
        <v-data-table
          v-else
          :headers="headers"
          :items="operationData.invoices"
          :items-per-page="pageSetup.pageSize"
          :footer-props="{
            'items-per-page-options': [8, 16],
          }"
          :options.sync="pageOptions"
          show-select
          v-model="operationData.selected"
          @item-selected="setSelection"
        >
          <template v-slot:header.data-table-select="{ on, props }">
            <v-simple-checkbox v-if="!isSingleSelect" v-bind="props" v-on="on"></v-simple-checkbox>
          </template>
          <template v-slot:item.tripRequestId="{ header, item }">
            <span class="font-weight-bold" style="color: #4066a1">{{ item.tripRequestId }}</span>
          </template>
          <template v-slot:item.total="{ header, item }">
            {{ toDollarAmount(item.total) }}
          </template>
          <template v-slot:item.status="{ header, item }">
            <v-chip
              data-cy="invoice-bulk-update-status"
              :item-id="item.id"
              :color="getStatus(header, item).color"
              small
              class="font-weight-bold"
              label
            >
              {{ getStatus(header, item).text }}
            </v-chip>
          </template>
          <template #item.data-table-select="{ isSelected, select, item }">
            <v-checkbox
              v-if="item.status !== selectedOperation.yields"
              :input-value="isSelected"
              @click="select(!isSelected)"
              hide-details
              dense
              data-cy="invoice-bulk-update-checkbox"
              :item-id="item.id"
            ></v-checkbox>
          </template>
        </v-data-table>
        <YellowBusBudgetCodesDialog :visible.sync="showYellowBusBudgetCodesDialog" />
      </div>
    </template>
    <template #actions>
      <v-btn v-if="showYellowBudgetCodeButton" color="primary" @click="showYellowBusBudgetCodesDialog = true"
        >Show Yellow Bus Budget Table</v-btn
      >
    </template>
  </TTDialog>
</template>

<script>
import TTDialog from '@/components/shared/Dialog.vue';
import invoiceApi from '@/apis/invoice';
import { mapGetters } from 'vuex';
import { colors, inputProps, toDollarAmount } from '@/util';
import YellowBusBudgetCodesDialog from '@/components/YellowBusBudgetCodesDialog';

export default {
  name: 'InvoiceOperationModal',
  components: {
    TTDialog,
    YellowBusBudgetCodesDialog,
  },
  props: {
    value: { type: Boolean, default: false },
    selectedOperation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      operationData: {
        isAllSelected: false,
        invoices: [],
        selected: [],
        deselected: [],
        invoicesTotal: 0,
        checkNumber: '',
        batchJournalNumber: '',
      },
      pageSetup: {
        currentPage: 1,
        pageSize: 8,
        length: 0,
      },
      pageOptions: {},
      isTableLoading: false,
      isProcessing: false,
      invoicePaymentColumns: [
        {
          text: 'Trip #',
          value: 'tripRequestId',
          align: 'start',
          show: true,
        },
        {
          text: 'Driver',
          value: 'driverName',
          align: 'start',
          show: true,
        },
        {
          text: 'Trip Type',
          value: 'tripType',
          align: 'start',
          show: true,
        },
        {
          text: 'Fund',
          value: 'fundingSourceName',
          align: 'start',
          show: true,
        },
        {
          text: 'Check #',
          value: 'checkNumber',
          align: 'start',
          show: true,
        },
        {
          text: 'Totals',
          value: 'total',
          align: 'end',
          show: true,
        },
        {
          text: 'Status',
          value: 'status',
          align: 'start',
          show: true,
          render: (item) => {
            return this.statusConfig[item.status] || {};
          },
        },
      ],
      toDollarAmount,
      colors,
      isDoneProcessing: false,
      failedUpdates: [],
      inputProps,
      showYellowBusBudgetCodesDialog: false,
    };
  },
  computed: {
    ...mapGetters('user', ['me']),
    ...mapGetters('config', ['invoiceConfig', 'yellowBusConfig']),
    ...mapGetters('invoice', ['invoiceColumns', 'statusConfig', 'invoiceFilters', 'selectedInvoices']),
    headers() {
      const columns = ['mass-send', 'mass-pay', 'mass-approve', 'mass-do-not-bill'].includes(
        this.selectedOperation.value
      )
        ? this.invoicePaymentColumns
        : this.invoiceColumns;

      return columns.reduce((acc, header) => {
        if (!header.show) return acc;

        acc.push(header);
        return acc;
      }, []);
    },
    isSingleSelect() {
      if (this.me.is.superAdmin || this.me.is.finance) return false;

      for (let role of Object.keys(this.me.is))
        if (this.me.is[role] && this.invoiceConfig.allowInvoiceStatusBatchUpdate[role]) return false;
      return true;
    },
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    selectedInvoicesTotal() {
      let retVal = 0;
      if (this.operationData.isAllSelected)
        retVal =
          this.operationData.invoicesTotal -
          this.operationData.deselected.reduce((acc, deselected) => {
            return acc + Number(deselected.total);
          }, 0);
      else retVal = this.operationData.selected.reduce((acc, selected) => acc + Number(selected.total), 0);

      return retVal.toFixed(2);
    },

    isFundingManagerOrSchoolFinance() {
      return this.me.is.fundingManager || this.me.is.schoolFinance;
    },

    selectedCount() {
      if (this.operationData.isAllSelected) return this.pageSetup.length - this.operationData.deselected.length;

      return this.operationData.selected.length;
    },

    showYellowBudgetCodeButton() {
      return !!this.yellowBusConfig?.displayYellowBusVehicleType;
    },
  },
  watch: {
    value(value) {
      if (value) {
        this.loadInvoices();
      } else {
        this.operationData = {
          isAllSelected: false,
          invoices: [],
          selected: [],
          deselected: [],
        };
        this.pageSetup.length = 0;
        this.pageSetup.currentPage = 1;
        this.isDoneProcessing = false;
        this.failedUpdates = [];
      }
    },
  },

  methods: {
    loadInvoices() {
      if (this.selectedInvoices.length) {
        // Use selected invoices
        this.operationData.invoices = this.selectedInvoices;
        return;
      }

      this.isTableLoading = true;
      const { itemsPerPage } = this.pageOptions;
      this.pageSetup.pageSize = itemsPerPage;
      const notInStatus = [this.selectedOperation.yields];
      if (this.isFundingManagerOrSchoolFinance) this.invoiceFilters.status = 'approved';

      if (['mass-send', 'mass-pay', 'mass-approve', 'mass-do-not-bill'].includes(this.selectedOperation.value)) {
        invoiceApi
          .getInvoicePayments({
            options: {
              page: {
                currentPage: this.pageSetup.currentPage,
                pageSize: this.pageSetup.pageSize,
              },
              filter: {
                notInStatus,
                ...this.invoiceFilters,
              },
            },
          })
          .then((response) => {
            this.pageSetup.length = response.total;
            this.operationData.invoices = response.data;
            this.operationData.invoicesTotal = response.invoicesTotal;
            this.keepTrackOfSelection();
          })
          .finally(() => {
            this.isTableLoading = false;
          });
      } else {
        invoiceApi
          .getInvoices({
            options: {
              page: {
                currentPage: this.pageSetup.currentPage,
                pageSize: this.pageSetup.pageSize,
              },
              sort: {
                column: 'tripRequestId',
                order: 'desc',
              },
              filter: {
                notInStatus,
                ...this.invoiceFilters,
              },
              getTotalTranscendsPagination: this.selectedOperation.showTotals,
            },
          })
          .then((response) => {
            this.pageSetup.length = response.total;
            this.operationData.invoices = response.data;
            this.operationData.invoicesTotal = response.invoicesTotal;
            this.keepTrackOfSelection();
          })
          .finally(() => {
            this.isTableLoading = false;
          });
      }
    },

    setSelection(item) {
      if (!item.value && this.operationData.isAllSelected) this.operationData.deselected.push(item.item);
      if (item.value && this.operationData.isAllSelected) {
        this.operationData.deselected = this.operationData.deselected.filter(
          (deselected) => deselected.id !== item.item.id
        );
      }
    },

    pageChanged({ itemsPerPage, page }) {
      this.$nextTick(() => {
        if (this.dialog) this.loadInvoices();
      });
    },

    keepTrackOfSelection() {
      if (this.operationData.isAllSelected) {
        this.operationData.selected = this.operationData.invoices.filter(
          (invoice) => !this.operationData.deselected.find((deselected) => deselected.id === invoice.id)
        );
      }
    },

    selectionChanged(value) {
      this.operationData.deselected = [];
      if (value) {
        this.operationData.selected = [...this.operationData.invoices];
      } else {
        this.operationData.selected = [];
      }
    },

    selectionChangedViaCheckbox(value) {
      this.operationData.isAllSelected = value.value;
      this.selectionChanged(value.value);
    },

    process() {
      const filteredInvoices = new Set(
        this.operationData.invoices
          .filter((invoice) => invoice.status !== this.selectedOperation.yields)
          .map((invoice) => invoice.id)
      );

      const ids = (
        this.operationData.isAllSelected ? this.operationData.deselected : this.operationData.selected
      ).reduce((acc, invoice) => {
        if (filteredInvoices.has(invoice.id)) acc.push(invoice.id);
        return acc;
      }, []);

      const idSet = new Set(ids);

      if (!ids.length && !this.operationData.isAllSelected) {
        this.$myalert.error('Please select at least one invoice to process');
        return;
      }
      this.isProcessing = true;

      if (this.isFundingManagerOrSchoolFinance) this.invoiceFilters.status = 'approved';
      const inputValue = {};

      if (this.selectedOperation.value === 'mass-send') inputValue.checkNumber = this.operationData.checkNumber;
      else if (this.selectedOperation.value === 'mass-pay')
        inputValue.batchJournalNumber = this.operationData.batchJournalNumber;

      const batchProcess = ['mass-send', 'mass-pay', 'mass-approve', 'mass-do-not-bill'].includes(
        this.selectedOperation.value
      )
        ? invoiceApi.batchUpdateInvoicePayment
        : invoiceApi.batchUpdateInvoice;

      batchProcess({
        ids,
        isAllSelected: this.operationData.isAllSelected,
        status: this.selectedOperation.yields,
        filters: this.invoiceFilters,
        inputValue,
      })
        .then((res) => {
          this.$myalert.success('Operation completed successfully');
          this.failedUpdates = res.failedUpdates;

          const failedSet = new Set(res.failedUpdates);
          this.operationData.invoices = this.operationData.invoices.map((invoice) =>
            idSet.has(invoice.id) && !failedSet.has(invoice.id)
              ? { ...invoice, status: this.selectedOperation.yields }
              : invoice
          );

          this.isDoneProcessing = true;
        })
        .catch((error) => {
          this.$myalert.error('An error occurred while processing the operation: ', error?.message);
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },

    getStatus(header, item) {
      const statusLabel = header.render(item);
      return Object.keys(statusLabel).length === 0 ? { color: '#D5D5D5', text: 'Accepted' } : statusLabel;
    },
    willCloseDialog(value) {
      if (!value && this.isDoneProcessing) this.$emit('updated', true);
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table-header,
::v-deep .v-data-table-header th {
  background-color: #bbdefb !important;
}
</style>
